import { useCallback, useEffect, useMemo, useState } from "react";
import { useFolhaPagamentoItensVerbaPagination } from "../hooks/FolhaPagamento_itensverba/pagination";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { FolhaPagamentoDetalhe } from "../../domain/models/folha_pagamento_detalhe";
import { ItemFolhaPagamentoVerba } from "../../domain/models/item_folha_pagamento_verba";
import { ItemFolhaPagamentoDetalhe } from "../../domain/models/item_folha_pagamento_detalhe";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { ItemFolhaPagamentoVerbaForm, ItemFolhaPagamentoVerbaSchema } from "../schemas/FolhaPagamento_itensverba/item_folha_pagamento_verba";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFolhaPagamentoItensVerbaFilter } from "../hooks/FolhaPagamento_itensverba/filter";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * LoadItemFolhaPagamentoVerbasState
 */

type LoadItemFolhaPagamentoVerbasState = {
  idItemFolhaPagamento?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useFolhaPagamentoItensVerbaViewModel
 */

export const useFolhaPagamentoItensVerbaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    rh: {
      createItemFolhaPagamentoVerbaUseCase,
      updateItemFolhaPagamentoVerbaUseCase,
      deleteItemFolhaPagamentoVerbaUseCase,
      getItemVerbaFuncionarioDropDownUseCase,
      getAllItemFolhaPagamentoVerbaUseCase,
      getFolhaPagamentoDetalheUseCase,
      getItemFolhaPagamentoUseCase,
    },
  } = useDependencyInjection();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [selectedIdItemFolhaPagamento, setSelectedIdItemFolhaPagamento] = useState<number>();
  const [selectedFolhaPagamento, setSelectedFolhaPagamento] = useState<FolhaPagamentoDetalhe>();
  const [selectedItemFolhaPagamento, setSelectedItemFolhaPagamento] = useState<ItemFolhaPagamentoDetalhe>();
  const [selectedItemFolhaPagamentoVerba, setSelectedItemFolhaPagamentoVerba] = useState<ItemFolhaPagamentoVerba>();
  const [itemFolhaPagamentoVerbas, setItemFolhaPagamentoVerbas] = useState<ItemFolhaPagamentoVerba[]>();

  const [updatedItemFolhaPagamentoVerbaIndex, setUpdatedItemFolhaPagamentoVerbaIndex] = useState<number[]>([]);

  const [openCadastroItemFolhaPagamentoVerbaModal, setOpenCadastroItemFolhaPagamentoVerbaModal] = useState(false);
  const [openDeleteItemFolhaPagamentoVerbaModal, setOpenDeleteItemFolhaPagamentoVerbaModal] = useState(false);

  const hookForm = useForm<ItemFolhaPagamentoVerbaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ItemFolhaPagamentoVerbaSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: setErrorMessage,
  });

  const filters = useFolhaPagamentoItensVerbaFilter();
  const pagination = useFolhaPagamentoItensVerbaPagination();

  const valorTotal = useMemo(() => {
    if (!itemFolhaPagamentoVerbas) return "R$ 0,00"

    const finalValue = itemFolhaPagamentoVerbas.reduce((acc, v) => {
      const quant = v.quantidade ?? 0;
      const valorUnit = v.valorUnitario ?? 0;
      const signFactor = v.tipo === 1 ? -1 : 1;
      return acc + (signFactor * quant * valorUnit);
    }, 0);

    return `R$ ${finalValue.toFixed(2).replace(".", ",")}`;
  }, [
    itemFolhaPagamentoVerbas,
  ]);

  /**
   * Funções de carregamento
   */

  const loadItensVerbaFuncionarioDropDown = useCallback(async (idFuncionario: number) => {
    try {
      var itensVerbaFuncionario = await getItemVerbaFuncionarioDropDownUseCase
        .getAllItemVerbaFuncionarioDropDown(idFuncionario);

      useFolhaPagamentoItensVerbaFilter.getState().setItensVerbaFuncionario(itensVerbaFuncionario);
    } catch (e: unknown) {
      useFolhaPagamentoItensVerbaFilter.getState().setItensVerbaFuncionario([]);
      throw e;
    }
  }, [
    getItemVerbaFuncionarioDropDownUseCase,
  ]);

  const loadFolhaPagamento = useCallback(async (idItemFolhaPagamento?: number) => {
    try {
      const filteredIdItemFolhaPagamento = idItemFolhaPagamento ?? selectedIdItemFolhaPagamento ?? 0;

      const folhaPagamento = await getFolhaPagamentoDetalheUseCase
        .getFolhaPagamentoDetalhe(filteredIdItemFolhaPagamento);

      setSelectedFolhaPagamento(folhaPagamento);
    } catch (e: unknown) {
      throw e;
    }
  }, [
    selectedIdItemFolhaPagamento,
    getFolhaPagamentoDetalheUseCase,
  ]);

  const loadItemFolhaPagamento = useCallback(async (idItemFolhaPagamento?: number) => {
    try {
      const filteredIdItemFolhaPagamento = idItemFolhaPagamento ?? selectedIdItemFolhaPagamento ?? 0;

      const itemFolhaPagamento = await getItemFolhaPagamentoUseCase
        .getItemFolhaPagamento(filteredIdItemFolhaPagamento);

      setSelectedItemFolhaPagamento(itemFolhaPagamento);
    } catch (e: unknown) {
      throw e;
    }
  }, [
    selectedIdItemFolhaPagamento,
    getItemFolhaPagamentoUseCase,
  ]);

  const loadItemFolhaPagamentoVerbas = useCallback(async (funcState?: LoadItemFolhaPagamentoVerbasState) => {
    setItemFolhaPagamentoVerbas(undefined);
    setTimeout(async () => {
      try {
        const filteredIdItemFolhaPagamento = funcState?.idItemFolhaPagamento ?? selectedIdItemFolhaPagamento ?? 0;
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllItemFolhaPagamentoVerbaUseCase.getAllItemFolhaPagamentoVerba(
          filteredIdItemFolhaPagamento,
          filteredPage,
          filteredRowsPerPage,
        );

        setItemFolhaPagamentoVerbas(getAllItemFolhaPagamentoVerbaUseCase.items);

        pagination.setTotalItems(getAllItemFolhaPagamentoVerbaUseCase.totalItems);
      } catch (e: unknown) {
        setItemFolhaPagamentoVerbas([]);
        throw e;
      }
    }, 0);

  }, [
    selectedIdItemFolhaPagamento,
    pagination,
    getAllItemFolhaPagamentoVerbaUseCase,
  ]);

  /**
   * UseEffects
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);

      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = locationId ?? null;

      if (id === null) throw new Error('Falha ao obter id da folha de pagamento');

      await loadFolhaPagamento(Number(id));
      await loadItemFolhaPagamento(Number(id));

      useFolhaPagamentoItensVerbaPagination.setState({ page: 1 });
      await loadItemFolhaPagamentoVerbas({ idItemFolhaPagamento: Number(id), page: 1 });

      setSelectedIdItemFolhaPagamento(Number(id));

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar folha de pagamento!")[0]);
    }
  });

  useEffect(() => {
    if (selectedFolhaPagamento) {
      (async function () {
        try {
          setLoading(true);
          await loadItensVerbaFuncionarioDropDown(selectedFolhaPagamento.idFuncionario);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao carregar pagina!'));
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [
    selectedFolhaPagamento,
    pushErrorNotification,
    loadItensVerbaFuncionarioDropDown,
  ]);

  /**
   * Eventos
   */

  const handleSubmitItemFolhaPagamentoVerba = useCallback(async (form: ItemFolhaPagamentoVerbaForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedItemFolhaPagamento) {
        await createItemFolhaPagamentoVerbaUseCase.createItemFolhaPagamentoVerba(
          selectedItemFolhaPagamento.idItemFolhaPagamento,
          form.idItensVerbaFuncionario,
        );

        await loadItemFolhaPagamentoVerbas();
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao cadastro verba do item de folha de pagamento!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
      setOpenCadastroItemFolhaPagamentoVerbaModal(false);
    }
  }, [
    selectedItemFolhaPagamento,
    createItemFolhaPagamentoVerbaUseCase,
    loadItemFolhaPagamentoVerbas,
    pushErrorNotification,
    setPageSaving,
    pushInfoNotification,
  ]);

  const handleSubmitItemsFolhaPagamentoVerbaChange = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (itemFolhaPagamentoVerbas && itemFolhaPagamentoVerbas.length > 0) {
        for (let index of updatedItemFolhaPagamentoVerbaIndex) {
          const item = itemFolhaPagamentoVerbas[index];

          await updateItemFolhaPagamentoVerbaUseCase.updateItemFolhaPagamentoVerba(
            item.idItemFolhaPagamentoVerba,
            item.valorUnitario,
            item.quantidade,
          );
        }
        await loadItemFolhaPagamentoVerbas();

        setUpdatedItemFolhaPagamentoVerbaIndex([]);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao cadastrar verba do item de folha de pagamento!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
      setOpenCadastroItemFolhaPagamentoVerbaModal(false);
    }
  }, [
    itemFolhaPagamentoVerbas,
    updatedItemFolhaPagamentoVerbaIndex,
    updateItemFolhaPagamentoVerbaUseCase,
    setPageSaving,
    loadItemFolhaPagamentoVerbas,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      if (selectedFolhaPagamento) {
        await loadItensVerbaFuncionarioDropDown(selectedFolhaPagamento.idFuncionario);
      }

      await loadFolhaPagamento();
      await loadItemFolhaPagamento();
      await loadItemFolhaPagamentoVerbas();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    selectedFolhaPagamento,
    loadItensVerbaFuncionarioDropDown,
    loadFolhaPagamento,
    loadItemFolhaPagamento,
    loadItemFolhaPagamentoVerbas,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    if (selectedFolhaPagamento) {
      backOrNavigate(-1, { alternativeRoute: '/FolhaPagamento_cadastro?id=' + selectedFolhaPagamento.idFolhaPagamento });
    } else {
      backOrNavigate(-1, { alternativeRoute: '/FolhaPagamento_lista' });
    }
  }, [
    selectedFolhaPagamento,
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useFolhaPagamentoItensVerbaPagination.getState().setPage(page + 1);
      await loadItemFolhaPagamentoVerbas({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadItemFolhaPagamentoVerbas,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useFolhaPagamentoItensVerbaPagination.getState().setPage(1);
      useFolhaPagamentoItensVerbaPagination.getState().setRowsPerPage(rowsPerPage);
      await loadItemFolhaPagamentoVerbas({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadItemFolhaPagamentoVerbas,
  ]);

  const handleCadastroItemFolhaPagamentoModalOpenState = useCallback((openState: boolean) => {
    setOpenCadastroItemFolhaPagamentoVerbaModal(openState);
  }, []);

  const handleDeleteFolhaPagamentoVerbaOpenState = useCallback((idItemFolhaPagamentoVerba: number | null, openState: boolean) => {
    if (openState) {
      if (idItemFolhaPagamentoVerba && itemFolhaPagamentoVerbas) {
        setSelectedItemFolhaPagamentoVerba(
          itemFolhaPagamentoVerbas.find(x => x.idItemFolhaPagamentoVerba === idItemFolhaPagamentoVerba));
      } else {
        setSelectedItemFolhaPagamentoVerba(undefined);
      }
    }
    setOpenDeleteItemFolhaPagamentoVerbaModal(openState);
  }, [
    itemFolhaPagamentoVerbas,
  ]);

  const handleConfirmarDeletarItemFolhaPagamentoVerba = useCallback(async () => {
    try {
      setSaving(true);

      if (selectedItemFolhaPagamentoVerba) {
        await deleteItemFolhaPagamentoVerbaUseCase.deleteItemFolhaPagamentoVerba(
          selectedItemFolhaPagamentoVerba.idItemFolhaPagamentoVerba,
        );

        await loadItemFolhaPagamentoVerbas();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao deletar verba de item de folha de pagamento!'));
    } finally {
      setSaving(false);
      setOpenDeleteItemFolhaPagamentoVerbaModal(false);
    }
  }, [
    selectedItemFolhaPagamentoVerba,
    deleteItemFolhaPagamentoVerbaUseCase,
    pushErrorNotification,
    loadItemFolhaPagamentoVerbas,
    pushInfoNotification,
  ]);

  const handleQuantidadeChange = useCallback((idItemFolhaPagamentoVerba: number, valor: number | null) => {
    setItemFolhaPagamentoVerbas(list => {
      if (list === undefined) {
        return list;
      } else {
        const index = list.findIndex(x => x.idItemFolhaPagamentoVerba === idItemFolhaPagamentoVerba);
        if (index >= 0) {
          const quantidade = valor;
          const valorUnitario = list[index].valorUnitario;
          const valorVerba = quantidade !== null && valorUnitario !== null ? quantidade * valorUnitario : null;

          const valorTotalOriginal = list[index].valorTotalOriginal ?? 0;
          const valorVerbaOriginal = list[index].valorVerbaOriginal ?? 0;
          const valorTotal = valorVerba !== null ? (valorTotalOriginal - valorVerbaOriginal) + valorVerba : null;

          list[index].quantidade = quantidade;
          list[index].valorVerba = valorVerba;
          list[index].valorTotal = valorTotal;

          setUpdatedItemFolhaPagamentoVerbaIndex(indexList => {
            if (!indexList.includes(index)) indexList.push(index);
            return [...indexList];
          });
        }
        return [...list];
      }
    });
  }, [
  ]);

  const handleValorUnitarioChange = useCallback((idItemFolhaPagamentoVerba: number, valor: number | null) => {
    setItemFolhaPagamentoVerbas(list => {
      if (list === undefined) {
        return list;
      } else {
        const index = list.findIndex(x => x.idItemFolhaPagamentoVerba === idItemFolhaPagamentoVerba);
        if (index >= 0) {
          const quantidade = list[index].quantidade;
          const valorUnitario = valor;
          const valorVerba = quantidade !== null && valorUnitario !== null ? quantidade * valorUnitario : null;

          const valorTotalOriginal = list[index].valorTotalOriginal ?? 0;
          const valorVerbaOriginal = list[index].valorVerbaOriginal ?? 0;
          const valorTotal = valorVerba !== null ? (valorTotalOriginal - valorVerbaOriginal) + valorVerba : null;

          list[index].valorUnitario = valorUnitario;
          list[index].valorVerba = valorVerba;
          list[index].valorTotal = valorTotal;

          setUpdatedItemFolhaPagamentoVerbaIndex(indexList => {
            if (!indexList.includes(index)) indexList.push(index);
            return [...indexList];
          });
        }
        return [...list];
      }
    });
  }, [
  ]);

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (selectedIdItemFolhaPagamento) {
        changeHistory.handleOpenChangeHistory(selectedIdItemFolhaPagamento, LogTipoTabela.ITEM_FOLHA_PAGAMENTO, true);
      }
    }
  }, [
    selectedIdItemFolhaPagamento,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    selectedFolhaPagamento: selectedFolhaPagamento ?? null,
    selectedItemFolhaPagamento: selectedItemFolhaPagamento ?? null,
    itemFolhaPagamentoVerbas: itemFolhaPagamentoVerbas ?? [],
    openCadastroItemFolhaPagamentoVerbaModal,
    openDeleteItemFolhaPagamentoVerbaModal,
    hookForm,
    filters,
    pagination,
    changeHistory,
    valorTotal,
    handleSubmitItemFolhaPagamentoVerba,
    handleSubmitItemsFolhaPagamentoVerbaChange,
    handleReload,
    handleBack,
    handlePageChange,
    handleRowsPerPageChange,
    handleCadastroItemFolhaPagamentoModalOpenState,
    handleDeleteFolhaPagamentoVerbaOpenState,
    handleConfirmarDeletarItemFolhaPagamentoVerba,
    handleQuantidadeChange,
    handleValorUnitarioChange,
    handleOpenChangeHistory,
  };
}